import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Flask, Plus, CaretRight, ClockCounterClockwise, GitBranch, CaretDown,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/enterprise/StatusBadge';
import { EmptyState } from '@/components/enterprise/EmptyState';
import { devApi, PROTO_TONE } from './devApi';
import { RevisionDialog, CreatePrototypeDialog } from './PrototypeDialogs';
import { jstDateTime } from '@/utils/jstDate';

const fmt = (s) => (s ? jstDateTime(s) : '—');

const KIND_LABEL = {
  prototype: '試作', install: '投入', validation: '検証', revision: '改訂',
  returned: '返却', scrapped: 'スクラップ', rework: '手直し', hold: '保留',
  reuse_other: '他案件で使用可',
};

export const RevisionTimeline = ({ revisions, testid }) => {
  if (!revisions || revisions.length === 0) {
    return (
      <p className="px-4 py-3 text-sm text-zinc-600" data-testid={`${testid}-empty`}>
        図番のある部品がまだありません。
      </p>
    );
  }
  return (
    <div className="divide-y divide-zinc-200" data-testid={testid}>
      {revisions.map((r) => (
        <div key={r.part_id} className="px-4 py-2.5" data-testid={`${testid}-part-${r.part_id}`}>
          <p className="text-sm font-semibold text-zinc-900">
            {r.part_name} <span className="text-xs font-normal text-zinc-600">{r.drawing_no || ''}</span>
          </p>
          <div className="mt-1 flex flex-wrap gap-2">
            {r.versions.map((v, i) => (
              <div key={i} className={`rounded-md border px-2 py-1 text-xs ${
                v.current ? 'border-teal-400 bg-teal-50' : 'border-zinc-300 bg-zinc-50'}`}
                data-testid={`${testid}-version-${r.part_id}-${i}`}>
                <p className="font-semibold text-zinc-900">
                  {v.revision || '版なし'}{v.current ? '（現在）' : ''}
                </p>
                {v.used_in.length === 0 ? (
                  <p className="text-zinc-500">使用した試作なし</p>
                ) : v.used_in.map((u) => (
                  <p key={u.prototype_id} className="text-zinc-700">
                    {u.name} — {u.result_label || u.status_label}
                  </p>
                ))}
                {v.note && <p className="text-zinc-500">理由: {v.note}</p>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

/** 試作と検証（フェーズ3） — 試作ごとの実績・検証・改訂ループ。 */
export const PrototypeSection = ({ devId, canEdit, plannedParts, onChanged, guidance }) => {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [openTl, setOpenTl] = useState(false);
  const [revOpen, setRevOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(() => {
    devApi.prototypes(devId).then(setData).catch(() => toast.error('試作を読み込めませんでした'));
    devApi.timeline(devId).then((d) => setEvents(d.events || [])).catch(() => {});
  }, [devId]);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <p className="text-sm text-zinc-600" data-testid="dev-proto-loading">試作を読み込み中…</p>;
  }
  const blocked = !!guidance && guidance.can_start_prototype === false;
  const s = data.summary;
  // 0 の状態は出さない。合計だけは常に出す（「何件あるか」は0でも意味がある）。
  const protoStates = [
    { key: 'open', label: '進行中', value: s.open, cls: 'text-blue-800' },
    { key: 'passed', label: '合格', value: s.passed, cls: 'text-emerald-800' },
    { key: 'passed_with_issues', label: '限定合格', value: s.passed_with_issues,
      cls: 'text-teal-800', testid: 'dev-proto-passed-with-issues' },
    { key: 'failed', label: '不合格', value: s.failed, cls: 'text-rose-800' },
    { key: 'needs_revision', label: '図面修正が必要', value: s.needs_revision, cls: 'text-amber-800' },
    { key: 'cancelled', label: '取消', value: s.cancelled, cls: 'text-zinc-500',
      testid: 'dev-proto-cancelled-count' },
    { key: 'validations', label: '検証記録', value: s.validations, cls: 'text-zinc-600' },
  ].filter((x) => Number(x.value) > 0);
  const partStates = [
    { key: 'returned', label: '返却', value: s.returned_qty, cls: '' },
    { key: 'reuse_other', label: '他案件で使用可', value: s.reuse_other_qty,
      cls: '', testid: 'dev-proto-reuse-other-qty' },
    { key: 'scrapped', label: 'スクラップ', value: s.scrapped_qty, cls: 'text-rose-800' },
    { key: 'rework', label: '手直し', value: s.rework_qty, cls: 'text-amber-800' },
  ].filter((x) => Number(x.value) > 0);

  return (
    <section className="space-y-3" data-testid="dev-prototypes">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-lg font-bold text-zinc-900">
          <Flask size={18} weight="duotone" className="text-zinc-500" /> 試作と検証
        </h2>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" disabled={!canEdit || !(plannedParts || []).length}
            onClick={() => setRevOpen(true)} data-testid="dev-proto-revision-btn">
            <GitBranch size={14} className="mr-1" /> 改訂を作成
          </Button>
          <Button size="sm" disabled={!canEdit || blocked} onClick={() => setAddOpen(true)}
            title={blocked ? (guidance.prototype_blockers || []).join(' / ') : undefined}
            data-testid="dev-proto-add-btn">
            <Plus size={14} className="mr-1" /> 試作を登録
          </Button>
        </div>
      </div>

      {blocked && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3"
          data-testid="dev-proto-blocked">
          <p className="text-sm font-semibold text-amber-900">
            まだ試作を始められません。先に次を済ませてください。
          </p>
          <ul className="mt-0.5 space-y-0.5 text-sm text-amber-900">
            {(guidance.prototype_blockers || []).map((b, i) => <li key={i}>・{b}</li>)}
          </ul>
        </div>
      )}

      {/* ルール2: 合計と、いま0でない状態だけを出す。0が並ぶと「読むものが無い」と
          学習してしまい、本当に数字が入ったときに見なくなる。0に戻った状態は
          また消える（場所を空けて残さない）。軸の分け方（試作=件／部品=数量）は
          正しさの修正なので維持する。 */}
      <div className="flex flex-wrap items-stretch gap-2 text-xs font-semibold"
        data-testid="dev-proto-summary">
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-300 bg-white px-4 py-3 shadow-sm">
          <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-bold text-zinc-600">
            試作（件）
          </span>
          <span>合計 {s.prototypes}</span>
          {protoStates.map((x) => (
            <span key={x.key} className={x.cls} data-testid={x.testid}>
              {x.label} {x.value}
            </span>
          ))}
        </div>
        {(partStates.length > 0 || s.pending_disposition > 0 || s.hold_qty > 0) && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-zinc-400 bg-zinc-50 px-4 py-3 shadow-sm">
            <span className="rounded bg-zinc-200 px-1.5 py-0.5 text-[10px] font-bold text-zinc-700">
              部品（数量）
            </span>
            {partStates.map((x) => (
              <span key={x.key} className={x.cls} data-testid={x.testid}>
                {x.label} {x.value}
              </span>
            ))}
            {(s.pending_disposition > 0 || s.hold_qty > 0) && (
              <span className="text-amber-900" data-testid="dev-proto-pending-disposition">
                処置が未記録 {s.pending_disposition}件
                {s.hold_qty > 0 ? `（保留 ${s.hold_qty} を含む）` : ''}
              </span>
            )}
          </div>
        )}
      </div>

      {((data.failures || []).length > 0 || (data.disposition_causes || []).length > 0) && (
        <div className="rounded-lg border border-amber-300 bg-amber-50/60 px-4 py-3"
          data-testid="dev-proto-failures">
          <p className="text-sm font-bold text-amber-900">
            不具合の傾向（記録 {s.failure_records}件{s.recurrences ? ` · 再発 ${s.recurrences}件` : ''}）
          </p>
          <div className="mt-1 flex flex-wrap gap-2">
            {(data.failures || []).map((f) => (
              <span key={f.category} data-testid={`dev-proto-failure-cat-${f.category}`}
                className="rounded-md border border-amber-300 bg-white px-2 py-0.5 text-xs font-semibold text-amber-900">
                {f.label} {f.count}件
              </span>
            ))}
            {(data.failures || []).length === 0 && (
              <span className="text-xs text-amber-900">検証の不具合記録はまだありません。</span>
            )}
          </div>
          {(data.disposition_causes || []).length > 0 && (
            <>
              <p className="mt-2 text-xs font-bold text-amber-900">
                部品を捨てた・作り直した原因（スクラップ・手直し）
              </p>
              <div className="mt-1 flex flex-wrap gap-2" data-testid="dev-proto-disp-causes">
                {data.disposition_causes.map((c) => (
                  <span key={c.category} data-testid={`dev-proto-disp-cause-${c.category}`}
                    className="rounded-md border border-rose-300 bg-white px-2 py-0.5 text-xs font-semibold text-rose-900">
                    {c.label} {c.count}件 / {c.qty}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* 取消の理由は不具合傾向とは**別の枠**に出す（非・不具合を傾向に混ぜない） */}
      {(data.cancel_causes || []).length > 0 && (
        <div className="rounded-lg border border-zinc-300 bg-zinc-50 px-4 py-3"
          data-testid="dev-proto-cancel-causes">
          <p className="text-sm font-bold text-zinc-700">
            取消の理由（{s.cancelled}件）
          </p>
          <div className="mt-1 flex flex-wrap gap-2">
            {data.cancel_causes.map((c) => (
              <span key={c.code} data-testid={`dev-proto-cancel-cause-${c.code}`}
                className="rounded-md border border-zinc-300 bg-white px-2 py-0.5 text-xs font-semibold text-zinc-700">
                {c.label} {c.count}件
              </span>
            ))}
          </div>
        </div>
      )}

      {data.prototypes.length === 0 ? (
        <EmptyState icon={Flask} title="試作はまだ登録されていません"
          description="試作を登録して、実際に使った部品と検証結果を残します。登録だけでは在庫は動きません。"
          testid="dev-proto-empty" />
      ) : (
        <div className="divide-y divide-zinc-200 rounded-lg border border-zinc-300 bg-white shadow-sm">
          {data.prototypes.map((p) => (
            <button key={p.id} type="button" data-testid={`dev-proto-row-${p.id}`}
              onClick={() => nav(`/engineering/developments/${devId}/prototypes/${p.id}`)}
              className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left hover:bg-teal-50/50">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-zinc-900">
                  <span className={p.cancelled ? 'text-zinc-500 line-through' : ''}>{p.name}</span>
                  {p.rebuild_of_name && (
                    <span className="ml-1.5 text-xs font-normal text-zinc-500">
                      （{p.rebuild_of_name} の作り直し）
                    </span>
                  )}
                  <span className="ml-2 text-xs font-normal text-zinc-600">
                    製作日 {p.build_date || '—'} · 製作台数 {p.build_quantity} ·{' '}
                    {(p.builder_names || []).join('、') || '—'}
                  </span>
                </p>
                <p className="truncate text-xs text-zinc-600">
                  投入部品 {p.installed_parts.length}件 · 検証 {p.validations.length}件
                  {p.drawing_revision_note ? ` · 図面 ${p.drawing_revision_note}` : ''}
                </p>
                {p.cancelled && (
                  <p className="mt-0.5 truncate text-xs font-semibold text-zinc-500"
                    data-testid={`dev-proto-cancelled-${p.id}`}>
                    取消（{p.cancel_reason_label}）· {p.cancel_reason}
                  </p>
                )}
                {!p.cancelled && !p.readiness.ready && (
                  <p className="mt-0.5 truncate text-xs font-semibold text-amber-800"
                    data-testid={`dev-proto-readiness-${p.id}`}>
                    {p.readiness.message}
                    {p.readiness.missing_parts.length > 0
                      ? `（${p.readiness.missing_parts.map((m) => `${m.name} 残${m.shortage}${m.uom}`).join('、')}）`
                      : ''}
                  </p>
                )}
              </div>
              <StatusBadge tone={p.cancelled ? 'neutral' : (PROTO_TONE[p.status] || 'neutral')}
                testid={`dev-proto-status-${p.id}`}>
                {p.cancelled ? `取消・${p.status_label}` : p.status_label}
              </StatusBadge>
              <CaretRight size={15} className="text-zinc-400" />
            </button>
          ))}
        </div>
      )}

      {/* 試作が0件のときは出さない。「どの版でどの試作を作ったか」の答えが自明に無く、
          5行の「版なし（現在）／使用した試作なし」は画面最大のブロックになるだけで、
          読み飛ばす癖をつけてしまう。空状態の箱も置かない（この節にはすでに
          「試作はまだ登録されていません」がある）。試作が1件でもあれば従来どおり。 */}
      {s.prototypes > 0 && (
        <div className="rounded-lg border border-zinc-300 bg-white shadow-sm">
          <div className="border-b border-zinc-200 bg-zinc-100 px-4 py-2">
            <p className="text-sm font-bold text-zinc-800">図面リビジョン履歴（どの版でどの試作を作ったか）</p>
          </div>
          <RevisionTimeline revisions={data.revisions} testid="dev-proto-revisions" />
        </div>
      )}

      {/* ルール1: 記録が1件も無いタイムラインは出さない（「（0件）」の見出しと
          「まだ記録はありません」は、この節の空状態と同じことを言うだけ）。 */}
      {events.length > 0 && (
        <div className="rounded-lg border border-zinc-300 bg-white shadow-sm">
          <button type="button" onClick={() => setOpenTl(!openTl)} data-testid="dev-proto-timeline-toggle"
            className="flex w-full items-center gap-2 border-b border-zinc-200 bg-zinc-100 px-4 py-2 text-left hover:bg-zinc-200/60">
            {openTl ? <CaretDown size={14} /> : <CaretRight size={14} />}
            <p className="flex items-center gap-1.5 text-sm font-bold text-zinc-800">
              <ClockCounterClockwise size={14} /> 試作タイムライン（{events.length}件）
            </p>
          </button>
          {openTl && (
            <ul className="divide-y divide-zinc-200" data-testid="dev-proto-timeline">
              {events.map((e, i) => (
                <li key={i} className="px-4 py-2 text-sm" data-testid={`dev-proto-timeline-${i}`}>
                  <span className="mr-2 text-xs font-semibold text-zinc-500">{fmt(e.at)}</span>
                  <StatusBadge tone="neutral" className="mr-2">{KIND_LABEL[e.kind] || e.kind}</StatusBadge>
                  <span className="font-medium text-zinc-900">{e.title}</span>
                  {e.detail && <p className="text-xs text-zinc-600">{e.detail}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <CreatePrototypeDialog devId={devId} open={addOpen} onOpenChange={setAddOpen}
        onDone={() => { load(); onChanged?.(); }} />
      <RevisionDialog devId={devId} open={revOpen} onOpenChange={setRevOpen}
        parts={plannedParts} onDone={() => { load(); onChanged?.(); }} />
    </section>
  );
};
